/*
  <mh-announcement data-style="marquee|slide" data-speed="35" data-interval="4"> — bandeau d'annonce à plusieurs
  messages (sections/mh-header.liquid, blocs « Message du bandeau »). Styles : assets/mh-header.css.
  - marquee : les messages défilent en continu (copies de la liste à la suite, masquées aux lecteurs d'écran),
    à vitesse constante (data-speed en px/s).
  - slide : un message à la fois, le suivant monte toutes les data-interval secondes.
  Pause au survol, au focus clavier et au toucher (un appui met en pause, le suivant ouvre le lien).
  Si le visiteur préfère moins d'animations : un message à la fois, en fondu.
  Dans l'éditeur, choisir un bloc affiche ce message.
*/
if (!customElements.get('mh-announcement')) {
  customElements.define('mh-announcement', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mh-announcement__track');
      this.items = [...this.track.children];
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mode = reduce ? 'fade' : this.dataset.style;
      this.classList.toggle('mh-announcement--fade', this.mode === 'fade');

      if (this.mode === 'marquee') this.setupMarquee();
      else this.setupRotation();

      // Pause : survol, focus clavier, toucher
      this.addEventListener('mouseenter', () => this.setPaused(true));
      this.addEventListener('mouseleave', () => this.setPaused(false));
      this.addEventListener('focusin', () => this.setPaused(true));
      this.addEventListener('focusout', () => this.setPaused(false));
      this.addEventListener('click', (event) => {
        if (this.lastPointer !== 'touch') return;
        if (!this.paused) {
          event.preventDefault();
          this.setPaused(true);
        }
      });
      this.addEventListener('pointerdown', (event) => { this.lastPointer = event.pointerType; });
      document.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' && !this.contains(event.target)) this.setPaused(false);
      });

      this.addEventListener('shopify:block:select', (event) => {
        const index = this.items.findIndex((item) => item.contains(event.target) || item === event.target);
        if (index >= 0 && this.mode !== 'marquee') this.show(index);
        this.setPaused(true);
      });
      this.addEventListener('shopify:block:deselect', () => this.setPaused(false));
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
    }

    setPaused(paused) {
      this.paused = paused;
      this.classList.toggle('is-paused', paused);
    }

    // Défilement continu : la liste est répétée jusqu'à dépasser la largeur du bandeau, puis doublée pour boucler
    setupMarquee() {
      const original = this.track.innerHTML;
      const build = () => {
        this.track.innerHTML = original;
        let copies = 1;
        while (this.track.scrollWidth < this.clientWidth && copies < 10) {
          this.track.insertAdjacentHTML('beforeend', original);
          copies += 1;
        }
        const width = this.track.scrollWidth;
        const clone = this.track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        // Les copies en plus de la première sont aussi masquées aux lecteurs d'écran
        [...this.track.children].slice(this.items.length).forEach((item) => {
          item.setAttribute('aria-hidden', 'true');
          item.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          if (item.matches('a')) item.setAttribute('tabindex', '-1');
        });
        clone.querySelectorAll(':scope > a').forEach((link) => link.setAttribute('tabindex', '-1'));
        this.querySelector('.mh-announcement__track + .mh-announcement__track')?.remove();
        this.track.after(clone);
        this.style.setProperty('--mh-announcement-duration', `${width / (Number(this.dataset.speed) || 35)}s`);
      };
      let lastWidth = 0;
      this.resizeObserver = new ResizeObserver(() => {
        if (Math.abs(this.clientWidth - lastWidth) < 2) return;
        lastWidth = this.clientWidth;
        build();
      });
      (document.fonts?.ready || Promise.resolve()).then(() => this.resizeObserver.observe(this));
    }

    // Un message à la fois
    setupRotation() {
      this.index = 0;
      this.items.forEach((item, i) => this.setVisible(item, i === 0));
      this.items[0].classList.add('is-active');
      const delay = (Number(this.dataset.interval) || 4) * 1000;
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.show((this.index + 1) % this.items.length);
      }, delay);
    }

    show(index) {
      if (index === this.index) return;
      const current = this.items[this.index];
      this.items.forEach((item) => item.classList.remove('is-leaving'));
      current.classList.replace('is-active', 'is-leaving');
      this.setVisible(current, false);
      this.items[index].classList.add('is-active');
      this.setVisible(this.items[index], true);
      this.index = index;
    }

    // Message masqué : ignoré par les lecteurs d'écran et la touche Tab
    setVisible(item, visible) {
      item.toggleAttribute('aria-hidden', !visible);
      if (item.matches('a')) item.tabIndex = visible ? 0 : -1;
    }
  });
}
